import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { MapContainer, TileLayer, FeatureGroup, Rectangle, useMap, ImageOverlay } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { 
  Satellite, Search, Download, Eye, FileText, Loader, X, 
  Calendar, Cloud, Sun, MapPin, Layers, RefreshCw, Radar,
  ChevronDown, ChevronUp, Check, AlertTriangle, Crosshair,
  Image as ImageIcon, GitCompare, Zap, Map as MapIcon, Activity, Target, Shield,
  Camera, Globe
} from 'lucide-react';

// Import professional intelligence report components
import {
  ClassificationBanner,
  ReportHeader,
  ExecutiveSummary,
  ReportSection,
  ThreatAssessmentPanel,
  SourceReliabilityMatrix,
  IntelSummaryCards,
  RecommendationsPanel,
  LocationContextPanel,
  ReportFooter,
  DetectedObjectCard,
  ExportActionsBar,
  IntelligenceReport
} from './IntelligenceReportUI';

// Map resize handler component
function MapResizeHandler() {
  const map = useMap();
  useEffect(() => {
    let timeoutId;
    try {
      timeoutId = setTimeout(() => {
        if (map && map.getContainer()) {
          map.invalidateSize();
        }
      }, 100);
    } catch (e) {
      console.warn('MapResizeHandler: Map not ready');
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [map]);
  return null;
}

// Fly to bounds component
function FlyToBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && map && map.getContainer()) {
      try {
        map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      } catch (e) {
        console.warn('FlyToBounds: Map not ready');
      }
    }
  }, [bounds, map]);
  return null;
}

export default function SatelliteView() {
  // State
  const [searchBounds, setSearchBounds] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewingImage, setViewingImage] = useState(null);
  const [showImintModal, setShowImintModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [analyzingImint, setAnalyzingImint] = useState(false);
  const [imintReport, setImintReport] = useState(null);
  const [changeReport, setChangeReport] = useState(null);
  const [analysisType, setAnalysisType] = useState('comprehensive');
  const [classification, setClassification] = useState('UNCLASSIFIED');
  const [expandedSections, setExpandedSections] = useState({});
  const [mapCenter] = useState([20, 0]);
  const [mapZoom] = useState(3);
  const [showSatelliteLayer, setShowSatelliteLayer] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const featureGroupRef = useRef(null);
  
  // Free satellite layers state
  const [freeLayers, setFreeLayers] = useState([]);
  const [selectedFreeLayer, setSelectedFreeLayer] = useState(null);
  const [layerDate, setLayerDate] = useState(new Date().toISOString().split('T')[0]);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [loadingLayers, setLoadingLayers] = useState(false);
  
  // Active layer on map state
  const [activeMapLayer, setActiveMapLayer] = useState(null);
  const [layerOpacity, setLayerOpacity] = useState(0.7);
  const [showOpacityControl, setShowOpacityControl] = useState(false);
  
  // NASA GIBS layer configurations
  const NASA_GIBS_LAYERS = {
    'modis_terra_truecolor': {
      id: 'MODIS_Terra_CorrectedReflectance_TrueColor',
      name: 'MODIS Terra True Color',
      maxZoom: 9
    },
    'modis_aqua_truecolor': {
      id: 'MODIS_Aqua_CorrectedReflectance_TrueColor', 
      name: 'MODIS Aqua True Color',
      maxZoom: 9
    },
    'viirs_snpp_truecolor': {
      id: 'VIIRS_SNPP_CorrectedReflectance_TrueColor',
      name: 'VIIRS SNPP True Color',
      maxZoom: 9
    }
  };
  
  // Load free satellite layers on mount
  useEffect(() => {
    loadFreeSatelliteLayers();
  }, []);
  
  const loadFreeSatelliteLayers = async () => {
    setLoadingLayers(true);
    try {
      const response = await axios.get(`${API}/satellite/layers`);
      setFreeLayers(response.data.layers || []);
    } catch (error) {
      console.error('Error loading satellite layers:', error);
    } finally {
      setLoadingLayers(false);
    }
  };
  
  // Get NASA GIBS tile URL for selected layer
  const getNasaGibsUrl = (layerId, date) => {
    const layerConfig = NASA_GIBS_LAYERS[layerId];
    if (!layerConfig) return null;
    return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${layerConfig.id}/default/${date}/GoogleMapsCompatible_Level${layerConfig.maxZoom}/{z}/{y}/{x}.jpg`;
  };
  
  // Toggle layer on map
  const toggleLayerOnMap = (layerId) => {
    if (activeMapLayer === layerId) {
      setActiveMapLayer(null);
      setShowOpacityControl(false);
    } else {
      setActiveMapLayer(layerId);
      setShowOpacityControl(true);
      setSelectedFreeLayer(layerId);
    }
  };
  
  // Get active layer name
  const getActiveLayerName = () => {
    const layer = freeLayers.find(l => l.id === activeMapLayer);
    return layer ? layer.name : '';
  };

  // Handle rectangle drawn on map
  const handleCreated = useCallback((e) => {
    const layer = e.layer;
    const bounds = layer.getBounds();
    const bbox = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    };
    setSearchBounds(bbox);
    setSearchResults([]);
    setSelectedImages([]);
    setViewingImage(null);
    
    // Auto-search when box is drawn
    searchSatelliteImages(bbox);
  }, []);

  // Handle shape deleted
  const handleDeleted = useCallback(() => {
    setSearchBounds(null);
    setSearchResults([]);
    setSelectedImages([]);
    setViewingImage(null);
  }, []);

  // Search for satellite images
  const searchSatelliteImages = async (bbox) => {
    setSearching(true);
    try {
      const response = await axios.post(`${API}/satellite/search`, {
        north: bbox.north,
        south: bbox.south,
        east: bbox.east,
        west: bbox.west
      });
      setSearchResults(response.data.images || []);
    } catch (error) {
      console.error('Error searching satellite images:', error);
      alert('Failed to search satellite images. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // Toggle image selection for change detection
  const toggleImageSelection = (image) => {
    setSelectedImages(prev => {
      const isSelected = prev.find(img => img.id === image.id);
      if (isSelected) {
        return prev.filter(img => img.id !== image.id);
      } else if (prev.length < 2) {
        return [...prev, image];
      } else {
        // Replace oldest selection
        return [prev[1], image];
      }
    });
  };

  // View image - open preview modal first
  const handleViewOnMap = (image) => {
    // Show preview modal - user can then choose to view on map from there
    setImagePreview(image);
  };

  // Actually display the image on the map (called from preview modal)
  const displayOnMap = (image) => {
    setViewingImage(image);
    setShowSatelliteLayer(true);
    setImagePreview(null);  // Close the preview modal
  };

  // Close image preview
  const closeImagePreview = () => {
    setImagePreview(null);
  };

  // Download satellite image 
  const handleDownload = async (image) => {
    setDownloading(image.id);
    try {
      // Use backend endpoint to get the satellite image
      const response = await axios.post(`${API}/satellite/download-image`, {
        satellite: image.satellite,
        bounds: image.bounds,
        acquisition_date: image.acquisition_date,
        provider: image.provider,
        image_id: image.id
      }, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${image.satellite}_${image.acquisition_date.split('T')[0]}_${image.id.slice(0,8)}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback - download comprehensive metadata with instructions
      const metadata = {
        image_info: {
          satellite: image.satellite,
          acquisition_date: image.acquisition_date,
          resolution: image.resolution,
          cloud_cover: `${image.cloud_cover_pct}%`,
          image_type: image.image_type,
          data_quality: image.data_quality
        },
        coverage: {
          bounds: image.bounds,
          center: { lat: image.center_lat, lon: image.center_lon },
          region: image.region
        },
        provider: {
          name: image.provider,
          data_portal: image.data_portal_url
        },
        download_instructions: `To download full-resolution imagery, visit the ${image.provider} data portal and search for ${image.satellite} imagery within the specified bounds and date.`
      };
      const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${image.satellite}_${image.acquisition_date.split('T')[0]}_metadata.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  // Generate IMINT report for single image
  const generateImintReport = async (image) => {
    setShowImintModal(true);
    setAnalyzingImint(true);
    setImintReport(null);
    
    try {
      const response = await axios.post(`${API}/satellite/imint-analysis`, {
        image_id: image.id,
        satellite: image.satellite,
        region: image.region || 'Custom Area',
        lat: image.center_lat,
        lon: image.center_lon,
        acquisition_date: image.acquisition_date,
        analysis_type: analysisType,
        classification_level: classification,
        bounds: image.bounds  // Include bounds for actual imagery fetch
      });
      setImintReport(response.data);
      setExpandedSections({
        executive: true,
        objects: true,
        threat: true,
        military: false,
        recommendations: true
      });
    } catch (error) {
      console.error('Error generating IMINT report:', error);
      alert('Failed to generate IMINT report. Please try again.');
      setShowImintModal(false);
    } finally {
      setAnalyzingImint(false);
    }
  };

  // Generate change detection report
  const generateChangeDetection = async () => {
    if (selectedImages.length !== 2) {
      alert('Please select exactly 2 images for change detection.');
      return;
    }
    
    setShowChangeModal(true);
    setAnalyzingImint(true);
    setChangeReport(null);
    
    try {
      const response = await axios.post(`${API}/satellite/change-detection`, {
        image1: {
          id: selectedImages[0].id,
          satellite: selectedImages[0].satellite,
          acquisition_date: selectedImages[0].acquisition_date,
          lat: selectedImages[0].center_lat,
          lon: selectedImages[0].center_lon
        },
        image2: {
          id: selectedImages[1].id,
          satellite: selectedImages[1].satellite,
          acquisition_date: selectedImages[1].acquisition_date,
          lat: selectedImages[1].center_lat,
          lon: selectedImages[1].center_lon
        },
        region: searchResults[0]?.region || 'Custom Area',
        classification_level: classification
      });
      setChangeReport(response.data);
      setExpandedSections({
        executive: true,
        changes: true,
        threat: true,
        recommendations: true
      });
    } catch (error) {
      console.error('Error generating change detection report:', error);
      alert('Failed to generate change detection report. Please try again.');
      setShowChangeModal(false);
    } finally {
      setAnalyzingImint(false);
    }
  };

  // Export report to PPTX
  const exportToPptx = async (reportId, type = 'imint') => {
    try {
      const endpoint = type === 'change' 
        ? `${API}/satellite/change-detection-export-pptx/${reportId}`
        : `${API}/satellite/imint-export-pptx/${reportId}`;
      
      const response = await axios.post(endpoint, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type.toUpperCase()}_Report_${reportId.slice(0,8)}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PPTX:', error);
      alert('Failed to export report');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCloudCoverColor = (pct) => {
    if (pct < 10) return 'text-[#00FF9D]';
    if (pct < 30) return 'text-yellow-400';
    return 'text-orange-400';
  };

  return (
    <div className="h-full flex flex-col" data-testid="satellite-view">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-black/30">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
              <Satellite className="w-7 h-7 text-[#00FF9D]" />
              SATELLITE INTELLIGENCE
            </h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Draw a box on the map to search for satellite imagery in your area of interest
            </p>
          </div>
          {selectedImages.length === 2 && (
            <button
              onClick={generateChangeDetection}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 hover:bg-purple-500/30 transition-all"
              data-testid="change-detection-btn"
            >
              <GitCompare className="w-4 h-4" />
              <span className="text-xs font-bold">GENERATE CHANGE DETECTION</span>
            </button>
          )}
        </div>
        
        {/* Instructions */}
        <div className="mt-3 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#00FF9D]/10 border border-[#00FF9D]/30">
            <span className="w-5 h-5 flex items-center justify-center bg-[#00FF9D]/20 text-[#00FF9D] font-bold">1</span>
            <span className="text-[#00FF9D]">Draw box on map</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30">
            <span className="w-5 h-5 flex items-center justify-center bg-cyan-500/20 text-cyan-400 font-bold">2</span>
            <span className="text-cyan-400">Review imagery results</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30">
            <span className="w-5 h-5 flex items-center justify-center bg-purple-500/20 text-purple-400 font-bold">3</span>
            <span className="text-purple-400">Download, View, or Analyze</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map Panel */}
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            style={{ background: '#0a0f14' }}
          >
            {/* Base map or satellite imagery layer */}
            {showSatelliteLayer ? (
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; Esri, Maxar, GeoEye, Earthstar Geographics'
              />
            ) : (
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
            )}
            
            {/* NASA GIBS Satellite Layer Overlay */}
            {activeMapLayer && getNasaGibsUrl(activeMapLayer, layerDate) && (
              <TileLayer
                url={getNasaGibsUrl(activeMapLayer, layerDate)}
                opacity={layerOpacity}
                attribution='&copy; NASA GIBS'
                maxZoom={9}
                tileSize={256}
              />
            )}
            
            <MapResizeHandler />
            
            {/* Drawing Controls */}
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={handleCreated}
                onDeleted={handleDeleted}
                draw={{
                  rectangle: {
                    shapeOptions: {
                      color: '#00FF9D',
                      weight: 2,
                      fillOpacity: 0.1
                    }
                  },
                  polygon: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false
                }}
                edit={{
                  edit: false,
                  remove: true
                }}
              />
            </FeatureGroup>
            
            {/* Show viewing image bounds */}
            {viewingImage && viewingImage.bounds && (
              <>
                <Rectangle
                  bounds={[
                    [viewingImage.bounds.south, viewingImage.bounds.west],
                    [viewingImage.bounds.north, viewingImage.bounds.east]
                  ]}
                  pathOptions={{
                    color: '#00F0FF',
                    weight: 3,
                    fillOpacity: 0.2,
                    fillColor: '#00F0FF'
                  }}
                />
                <FlyToBounds bounds={[
                  [viewingImage.bounds.south, viewingImage.bounds.west],
                  [viewingImage.bounds.north, viewingImage.bounds.east]
                ]} />
              </>
            )}
          </MapContainer>
          
          {/* Map Toggle Button */}
          <div className="absolute top-4 left-4 z-[1000]">
            <button
              onClick={() => setShowSatelliteLayer(!showSatelliteLayer)}
              className={`px-3 py-2 text-xs font-bold border transition-all ${
                showSatelliteLayer 
                  ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' 
                  : 'bg-black/80 text-zinc-400 border-white/10 hover:bg-white/10'
              }`}
            >
              {showSatelliteLayer ? 'SATELLITE VIEW' : 'MAP VIEW'}
            </button>
            <button
              onClick={() => setShowLayersPanel(!showLayersPanel)}
              className={`px-3 py-2 text-xs font-bold border transition-all ml-2 ${
                showLayersPanel 
                  ? 'bg-purple-500/20 text-purple-400 border-purple-500/50' 
                  : 'bg-black/80 text-zinc-400 border-white/10 hover:bg-white/10'
              }`}
              data-testid="layers-panel-btn"
            >
              <Layers className="w-4 h-4 inline mr-1" />
              FREE LAYERS
            </button>
          </div>
          
          {/* Free Satellite Layers Panel */}
          {showLayersPanel && (
            <div className="absolute top-16 left-4 z-[1000] bg-black/95 border border-purple-500/30 p-4 w-80 max-h-[500px] overflow-y-auto" data-testid="layers-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Satellite className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-bold text-purple-400">FREE SATELLITE LAYERS</span>
                </div>
                <button onClick={() => setShowLayersPanel(false)} className="text-zinc-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-[10px] text-zinc-500 mb-3">
                Real-time imagery from NASA GIBS, USGS, and Copernicus
              </p>
              
              {/* Date Selector */}
              <div className="mb-3">
                <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Imagery Date</label>
                <input
                  type="date"
                  value={layerDate}
                  onChange={(e) => setLayerDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-xs"
                  data-testid="layer-date-input"
                />
              </div>
              
              {/* Opacity Control - shown when layer is active */}
              {activeMapLayer && (
                <div className="mb-3 p-3 bg-[#00FF9D]/10 border border-[#00FF9D]/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-[#00FF9D] uppercase font-bold">Layer Opacity</span>
                    <span className="text-xs text-[#00FF9D] font-mono">{Math.round(layerOpacity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={layerOpacity * 100}
                    onChange={(e) => setLayerOpacity(e.target.value / 100)}
                    className="w-full h-2 bg-black/50 rounded-lg appearance-none cursor-pointer accent-[#00FF9D]"
                    data-testid="layer-opacity-slider"
                  />
                  <div className="flex justify-between text-[9px] text-zinc-500 mt-1">
                    <span>Transparent</span>
                    <span>Opaque</span>
                  </div>
                </div>
              )}
              
              {/* Layer Options */}
              <div className="space-y-2">
                {loadingLayers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader className="w-5 h-5 text-purple-400 animate-spin" />
                  </div>
                ) : freeLayers.length > 0 ? (
                  freeLayers.map((layer) => {
                    const isNasaGibs = layer.source === 'NASA GIBS';
                    const isActive = activeMapLayer === layer.id;
                    return (
                      <div
                        key={layer.id}
                        className={`p-3 border transition-all ${
                          isActive
                            ? 'bg-[#00FF9D]/20 border-[#00FF9D]/50'
                            : selectedFreeLayer === layer.id
                            ? 'bg-purple-500/20 border-purple-500/50'
                            : 'bg-black/30 border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`layer-${layer.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-white">{layer.name}</span>
                          {isActive && (
                            <span className="px-1.5 py-0.5 text-[9px] bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/50">
                              ACTIVE
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-zinc-500">{layer.source}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                            {layer.resolution}
                          </span>
                        </div>
                        
                        {/* Show on Map button - only for NASA GIBS layers */}
                        {isNasaGibs && (
                          <button
                            onClick={() => toggleLayerOnMap(layer.id)}
                            className={`w-full mt-2 py-1.5 text-[10px] font-bold uppercase transition-all ${
                              isActive
                                ? 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                                : 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20'
                            }`}
                            data-testid={`toggle-layer-${layer.id}`}
                          >
                            <Eye className="w-3 h-3 inline mr-1" />
                            {isActive ? 'HIDE FROM MAP' : 'SHOW ON MAP'}
                          </button>
                        )}
                        
                        {/* Info for non-GIBS layers */}
                        {!isNasaGibs && (
                          <div className="mt-2 text-[9px] text-zinc-500 italic">
                            Use Quick Access links below for {layer.source} imagery
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-xs text-zinc-500 text-center py-4">
                    No layers available
                  </div>
                )}
              </div>
              
              {/* External Links */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="text-[10px] text-zinc-500 uppercase mb-2">Quick Access</div>
                <div className="space-y-1">
                  <a 
                    href="https://worldview.earthdata.nasa.gov" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-[10px] text-cyan-400 hover:text-cyan-300"
                  >
                    NASA Worldview →
                  </a>
                  <a 
                    href="https://browser.dataspace.copernicus.eu" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-[10px] text-cyan-400 hover:text-cyan-300"
                  >
                    Copernicus Browser →
                  </a>
                  <a 
                    href="https://earthexplorer.usgs.gov" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-[10px] text-cyan-400 hover:text-cyan-300"
                  >
                    USGS Earth Explorer →
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* Active Layer Info Overlay */}
          {activeMapLayer && !showLayersPanel && (
            <div className="absolute top-16 left-4 z-[1000] bg-black/90 border border-[#00FF9D]/30 p-3 max-w-xs" data-testid="active-layer-overlay">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Satellite className="w-4 h-4 text-[#00FF9D]" />
                  <span className="text-xs font-bold text-[#00FF9D]">NASA GIBS LAYER</span>
                </div>
                <button 
                  onClick={() => { setActiveMapLayer(null); setShowOpacityControl(false); }}
                  className="text-zinc-500 hover:text-white"
                  data-testid="close-active-layer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[10px] text-zinc-400 space-y-1">
                <div className="font-bold text-white">{getActiveLayerName()}</div>
                <div>Date: {layerDate}</div>
                <div className="flex items-center gap-2">
                  <span>Opacity:</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={layerOpacity * 100}
                    onChange={(e) => setLayerOpacity(e.target.value / 100)}
                    className="w-20 h-1.5 bg-black/50 rounded-lg appearance-none cursor-pointer accent-[#00FF9D]"
                  />
                  <span className="text-[#00FF9D] font-mono">{Math.round(layerOpacity * 100)}%</span>
                </div>
              </div>
              <button
                onClick={() => setShowLayersPanel(true)}
                className="w-full mt-2 py-1 text-[9px] text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 transition-all"
              >
                Change Layer
              </button>
            </div>
          )}

          {/* Viewing Image Info Overlay */}
          {viewingImage && (
            <div className="absolute top-4 right-16 z-[1000] bg-black/90 border border-cyan-500/30 p-3 max-w-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-cyan-400">{viewingImage.satellite}</span>
                <button 
                  onClick={() => { setViewingImage(null); setShowSatelliteLayer(false); }}
                  className="text-zinc-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[10px] text-zinc-400 space-y-1">
                <div>Resolution: {viewingImage.resolution}</div>
                <div>Date: {new Date(viewingImage.acquisition_date).toLocaleDateString()}</div>
                <div>Cloud: {viewingImage.cloud_cover_pct}%</div>
                <div>Provider: {viewingImage.provider}</div>
              </div>
            </div>
          )}
          
          {/* Search Status Overlay */}
          {searching && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[1000]">
              <div className="bg-[#0a0f14] border border-[#00FF9D]/30 p-6 flex items-center gap-4">
                <Loader className="w-8 h-8 text-[#00FF9D] animate-spin" />
                <div>
                  <div className="text-lg font-bold">Searching Satellite Archives...</div>
                  <div className="text-xs text-zinc-400">Querying multiple satellite providers</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Search Bounds Info */}
          {searchBounds && !searching && (
            <div className="absolute bottom-4 left-4 bg-black/80 border border-white/10 p-3 z-[1000]">
              <div className="text-xs text-zinc-400 uppercase mb-1">Search Area</div>
              <div className="text-xs font-mono text-white">
                {searchBounds.south.toFixed(4)}°N to {searchBounds.north.toFixed(4)}°N
              </div>
              <div className="text-xs font-mono text-white">
                {searchBounds.west.toFixed(4)}°E to {searchBounds.east.toFixed(4)}°E
              </div>
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="w-[450px] border-l border-white/10 bg-black/30 flex flex-col overflow-hidden">
          {/* Panel Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#00FF9D]" />
                <span className="text-sm font-bold uppercase">Available Imagery</span>
              </div>
              {searchResults.length > 0 && (
                <span className="px-2 py-1 text-[10px] font-mono bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30">
                  {searchResults.length} IMAGES
                </span>
              )}
            </div>
            {selectedImages.length > 0 && (
              <div className="mt-2 text-xs text-purple-400">
                {selectedImages.length}/2 images selected for change detection
              </div>
            )}
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {!searchBounds ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <Crosshair className="w-16 h-16 text-zinc-600 mb-4" />
                <div className="text-sm text-zinc-400 mb-2">Draw a Rectangle on the Map</div>
                <div className="text-xs text-zinc-500">
                  Use the rectangle tool in the top-right corner of the map to select your area of interest
                </div>
              </div>
            ) : searchResults.length === 0 && !searching ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <Search className="w-16 h-16 text-zinc-600 mb-4" />
                <div className="text-sm text-zinc-400 mb-2">No Images Found</div>
                <div className="text-xs text-zinc-500">
                  Try drawing a larger area or a different location
                </div>
              </div>
            ) : (
              searchResults.map((image, idx) => {
                const isSelected = selectedImages.find(img => img.id === image.id);
                return (
                  <div 
                    key={image.id || idx}
                    className={`p-3 border transition-all ${
                      isSelected 
                        ? 'bg-purple-500/10 border-purple-500/50' 
                        : 'bg-black/30 border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`satellite-image-${idx}`}
                  >
                    {/* Image Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">{image.satellite}</span>
                        <span className="px-2 py-0.5 text-[10px] font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                          {image.resolution}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleImageSelection(image)}
                        className={`p-1.5 transition-all ${
                          isSelected 
                            ? 'bg-purple-500/20 text-purple-400' 
                            : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                        }`}
                        title="Select for change detection"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Image Details */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 text-zinc-500" />
                        <span className="text-zinc-300">{new Date(image.acquisition_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Cloud className={`w-3 h-3 ${getCloudCoverColor(image.cloud_cover_pct)}`} />
                        <span className={getCloudCoverColor(image.cloud_cover_pct)}>{image.cloud_cover_pct}% cloud</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Sun className="w-3 h-3 text-yellow-400" />
                        <span className="text-zinc-300">{image.sun_elevation}° sun</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-zinc-500" />
                        <span className="text-zinc-300">{image.center_lat?.toFixed(2)}°, {image.center_lon?.toFixed(2)}°</span>
                      </div>
                    </div>

                    {/* Image Type Tags */}
                    {image.image_type && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        <span className="px-2 py-0.5 text-[10px] font-mono bg-white/5 text-zinc-400 border border-white/10">
                          {image.image_type}
                        </span>
                        {image.archive && (
                          <span className="px-2 py-0.5 text-[10px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30">
                            ARCHIVE
                          </span>
                        )}
                        {!image.archive && (
                          <span className="px-2 py-0.5 text-[10px] font-mono bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30">
                            RECENT
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(image)}
                        disabled={downloading === image.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10 transition-all text-xs disabled:opacity-50"
                        title="Download satellite imagery"
                        data-testid={`download-btn-${image.id.slice(0,8)}`}
                      >
                        {downloading === image.id ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        <span>{downloading === image.id ? 'Loading...' : 'Download'}</span>
                      </button>
                      <button
                        onClick={() => handleViewOnMap(image)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-xs"
                        title="Preview and view on map"
                        data-testid={`view-btn-${image.id.slice(0,8)}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => generateImintReport(image)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30 hover:bg-[#00FF9D]/20 transition-all text-xs"
                        title="Generate AI IMINT report"
                        data-testid={`imint-btn-${image.id.slice(0,8)}`}
                      >
                        <Radar className="w-3.5 h-3.5" />
                        <span>IMINT</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Data Sources Footer */}
          <div className="p-3 border-t border-white/10 bg-black/50">
            <div className="text-[10px] text-zinc-500 uppercase mb-2">Data Sources</div>
            <div className="flex flex-wrap gap-1">
              {['Sentinel-2', 'Landsat-9', 'MODIS', 'Sentinel-1 SAR'].map(source => (
                <span key={source} className="px-2 py-1 text-[10px] font-mono bg-white/5 text-zinc-400 border border-white/10">
                  {source}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[2000] p-4">
          <div className="bg-[#0a0f14] border border-cyan-500/30 max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-[#0a0f14] border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ImageIcon className="w-6 h-6 text-cyan-400" />
                <div>
                  <h2 className="text-lg font-bold">SATELLITE IMAGERY PREVIEW</h2>
                  <p className="text-xs text-zinc-400">{imagePreview.satellite} • {new Date(imagePreview.acquisition_date).toLocaleDateString()} • {imagePreview.region}</p>
                </div>
              </div>
              <button 
                onClick={closeImagePreview}
                className="p-2 hover:bg-white/10 transition-all"
                data-testid="close-preview-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image Preview Content */}
            <div className="flex-1 overflow-auto p-4">
              {/* Satellite Image Preview using ESRI World Imagery */}
              <div className="relative bg-black/50 border border-white/10 mb-4 overflow-hidden" style={{ height: '400px' }}>
                <img 
                  src={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export?bbox=${imagePreview.bounds.west},${imagePreview.bounds.south},${imagePreview.bounds.east},${imagePreview.bounds.north}&bboxSR=4326&size=800,600&imageSR=4326&format=png&f=image`}
                  alt={`${imagePreview.satellite} imagery`}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="absolute inset-0 flex-col items-center justify-center bg-black/80 hidden">
                  <Satellite className="w-12 h-12 text-zinc-600 mb-3" />
                  <p className="text-zinc-400 text-sm">Preview image unavailable</p>
                  <p className="text-zinc-500 text-xs mt-1">View on map or use Download</p>
                </div>
                
                {/* Image info overlay */}
                <div className="absolute top-3 left-3 bg-black/80 px-3 py-2 border border-cyan-500/30">
                  <div className="text-[10px] text-cyan-400 font-bold">{imagePreview.satellite}</div>
                  <div className="text-[10px] text-zinc-400">{imagePreview.resolution} resolution</div>
                </div>
                
                {/* Coordinates overlay */}
                <div className="absolute bottom-3 left-3 bg-black/80 px-3 py-2 border border-white/10">
                  <div className="text-[10px] text-zinc-400 font-mono">
                    {imagePreview.bounds.south.toFixed(4)}°N, {imagePreview.bounds.west.toFixed(4)}°E
                  </div>
                  <div className="text-[10px] text-zinc-400 font-mono">
                    to {imagePreview.bounds.north.toFixed(4)}°N, {imagePreview.bounds.east.toFixed(4)}°E
                  </div>
                </div>
              </div>

              {/* Image Metadata */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-black/30 border border-white/10 p-3">
                  <div className="text-[10px] text-zinc-500 uppercase mb-1">Satellite</div>
                  <div className="text-sm font-bold text-white">{imagePreview.satellite}</div>
                </div>
                <div className="bg-black/30 border border-white/10 p-3">
                  <div className="text-[10px] text-zinc-500 uppercase mb-1">Resolution</div>
                  <div className="text-sm font-bold text-[#00FF9D]">{imagePreview.resolution}</div>
                </div>
                <div className="bg-black/30 border border-white/10 p-3">
                  <div className="text-[10px] text-zinc-500 uppercase mb-1">Cloud Cover</div>
                  <div className={`text-sm font-bold ${imagePreview.cloud_cover_pct < 20 ? 'text-[#00FF9D]' : imagePreview.cloud_cover_pct < 50 ? 'text-yellow-400' : 'text-orange-400'}`}>
                    {imagePreview.cloud_cover_pct}%
                  </div>
                </div>
                <div className="bg-black/30 border border-white/10 p-3">
                  <div className="text-[10px] text-zinc-500 uppercase mb-1">Provider</div>
                  <div className="text-sm font-bold text-cyan-400">{imagePreview.provider}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => displayOnMap(imagePreview)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500/10 text-cyan-400 border border-cyan-500/50 hover:bg-cyan-500/20 transition-all"
                  data-testid="view-on-map-btn"
                >
                  <MapIcon className="w-4 h-4" />
                  <span className="font-bold text-sm">VIEW ON MAP</span>
                </button>
                <button
                  onClick={() => handleDownload(imagePreview)}
                  disabled={downloading === imagePreview.id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20 transition-all disabled:opacity-50"
                  data-testid="download-image-btn"
                >
                  {downloading === imagePreview.id ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="font-bold text-sm">DOWNLOAD IMAGE</span>
                </button>
                <button
                  onClick={() => { closeImagePreview(); generateImintReport(imagePreview); }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/10 text-purple-400 border border-purple-500/50 hover:bg-purple-500/20 transition-all"
                  data-testid="analyze-image-btn"
                >
                  <Radar className="w-4 h-4" />
                  <span className="font-bold text-sm">AI ANALYSIS</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IMINT Analysis Modal */}
      {showImintModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] p-4">
          <div className="bg-[#0a0f14] border border-[#00FF9D]/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0a0f14] border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Radar className="w-6 h-6 text-cyan-400" />
                <div>
                  <h2 className="text-lg font-bold">AI IMINT ANALYSIS</h2>
                  <p className="text-xs text-zinc-400">Imagery Intelligence Report</p>
                </div>
              </div>
              <button 
                onClick={() => setShowImintModal(false)}
                className="p-2 hover:bg-white/10 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {analyzingImint ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="w-12 h-12 text-[#00FF9D] animate-spin mb-4" />
                  <div className="text-lg font-bold mb-2">Analyzing Imagery with AI...</div>
                  <div className="text-xs text-zinc-400">Detecting objects, assessing threats, generating report</div>
                </div>
              ) : imintReport ? (
                <IMINTReportDisplay 
                  report={imintReport} 
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  onExport={() => exportToPptx(imintReport.id, 'imint')}
                  onClose={() => setShowImintModal(false)}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Change Detection Modal */}
      {showChangeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] p-4">
          <div className="bg-[#0a0f14] border border-purple-500/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0a0f14] border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <GitCompare className="w-6 h-6 text-purple-400" />
                <div>
                  <h2 className="text-lg font-bold">AI CHANGE DETECTION</h2>
                  <p className="text-xs text-zinc-400">Comparing {selectedImages[0]?.satellite} and {selectedImages[1]?.satellite}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChangeModal(false)}
                className="p-2 hover:bg-white/10 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {analyzingImint ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="w-12 h-12 text-purple-400 animate-spin mb-4" />
                  <div className="text-lg font-bold mb-2">Analyzing Changes with AI...</div>
                  <div className="text-xs text-zinc-400">Comparing imagery, detecting changes, assessing significance</div>
                </div>
              ) : changeReport ? (
                <ChangeReportDisplay 
                  report={changeReport}
                  images={selectedImages}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  onExport={() => exportToPptx(changeReport.id, 'change')}
                  onClose={() => setShowChangeModal(false)}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Professional IMINT Report Display Component - NATO/OSINT Standard Design
function IMINTReportDisplay({ report, expandedSections, toggleSection, onExport, onClose }) {
  const [selectedObject, setSelectedObject] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Build source reliability data for the matrix
  const sourceReliability = [
    { name: 'Satellite Imagery', type: 'IMINT', reliability: 'B', accuracy: '2' },
    { name: 'Army Recognition DB', type: 'OSINT', reliability: 'B', accuracy: '2' },
    { name: 'Jane\'s Defence', type: 'OSINT', reliability: 'A', accuracy: '1' },
    { name: 'HRPlanesv2 Dataset', type: 'AI/ML', reliability: 'C', accuracy: '3' }
  ];

  // Count objects by category
  const objectCounts = {
    objects: report.detected_objects?.length || 0,
    threats: report.detected_objects?.filter(o => o.threat_level === 'CRITICAL' || o.threat_level === 'HIGH').length || 0,
    activities: report.activity_indicators?.length || 0,
    sources: report.reference_sources?.length || 4
  };

  // Extract recommendations as array
  const recommendations = Array.isArray(report.recommendations) 
    ? report.recommendations 
    : typeof report.recommendations === 'string' 
      ? [report.recommendations] 
      : ['Continue monitoring target area', 'Cross-reference with additional intelligence sources', 'Update threat assessment as new data becomes available'];

  return (
    <IntelligenceReport
      classification={report.classification || 'UNCLASSIFIED'}
      tlp="GREEN"
      title="IMAGERY INTELLIGENCE REPORT (IMINT)"
      subtitle={`${report.analysis_type || 'Military Assessment'} • ${report.region || 'Unknown Region'}`}
      reportId={report.id}
      date={report.generated_at?.split('T')[0] || new Date().toISOString().split('T')[0]}
      threatLevel={report.threat_level}
      confidence={report.overall_confidence_score}
    >
      {/* Intelligence Summary Cards */}
      <IntelSummaryCards data={objectCounts} />

      {/* Satellite Imagery Screenshots Section */}
      {report.imagery_screenshots && report.imagery_screenshots.length > 0 && (
        <ReportSection
          title="SATELLITE IMAGERY ANALYSIS"
          icon={Camera}
          expanded={expandedSections.imagery !== false}
          onToggle={() => toggleSection('imagery')}
          color="cyan"
          badge="SCREENSHOTS"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {report.imagery_screenshots.map((img, idx) => (
              <div key={idx} className="border border-white/10 bg-black/30">
                <div className="relative aspect-video">
                  <img 
                    src={img.url} 
                    alt={img.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/80 text-[9px] font-bold text-cyan-400 border border-cyan-500/30">
                    {img.scale}
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-xs font-bold text-white mb-1">{img.title}</div>
                  <div className="text-[10px] text-zinc-400">{img.description}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 p-2 bg-cyan-500/5 border border-cyan-500/20">
            <div className="text-[10px] text-cyan-400 flex items-center gap-2">
              <MapPin className="w-3 h-3" />
              Imagery Source: ESRI World Imagery / Satellite: {report.satellite} / Date: {report.acquisition_date}
            </div>
          </div>
        </ReportSection>
      )}

      {/* Country Military Intelligence Section */}
      {report.country_military_intel && report.country_military_intel.country !== 'Unknown Region' && (
        <ReportSection
          title="COUNTRY MILITARY INTELLIGENCE (OSINT)"
          icon={Globe}
          expanded={expandedSections.countryIntel !== false}
          onToggle={() => toggleSection('countryIntel')}
          color="purple"
          badge={report.country_military_intel.country?.toUpperCase()}
        >
          <div className="space-y-4">
            {/* Country Header */}
            <div className="flex items-center gap-4 p-3 bg-purple-500/10 border border-purple-500/30">
              {report.country_military_intel.flag && (
                <img src={report.country_military_intel.flag} alt="" className="w-12 h-8 object-cover border border-white/20" />
              )}
              <div>
                <div className="text-sm font-bold text-white">{report.country_military_intel.country}</div>
                <div className="text-[10px] text-zinc-400">
                  Global Military Strength Rank: #{report.country_military_intel.military_strength_rank}
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-[10px] text-zinc-500">Defense Budget</div>
                <div className="text-sm font-bold text-purple-400">{report.country_military_intel.defense_budget}</div>
              </div>
            </div>
            
            {/* Personnel */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-black/30 border border-white/10">
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Active Personnel</div>
                <div className="text-lg font-bold text-white">{report.country_military_intel.active_personnel}</div>
              </div>
              <div className="p-3 bg-black/30 border border-white/10">
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Reserve Personnel</div>
                <div className="text-lg font-bold text-white">{report.country_military_intel.reserve_personnel}</div>
              </div>
            </div>
            
            {/* Key Equipment */}
            {report.country_military_intel.key_equipment && (
              <div className="space-y-2">
                <div className="text-xs font-bold text-zinc-400 uppercase">Key Military Equipment</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(report.country_military_intel.key_equipment).map(([type, data]) => (
                    <div key={type} className="p-2 bg-black/30 border border-white/10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-zinc-500 uppercase">{type}</span>
                        <span className="text-xs font-bold text-[#00FF9D]">{data.count}</span>
                      </div>
                      <div className="text-[9px] text-zinc-400">
                        {data.primary?.slice(0, 3).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Air Defense Systems */}
            {report.country_military_intel.air_defense_systems && (
              <div>
                <div className="text-xs font-bold text-zinc-400 uppercase mb-2">Air Defense Systems</div>
                <div className="flex flex-wrap gap-2">
                  {report.country_military_intel.air_defense_systems.map((sys, idx) => (
                    <span key={idx} className="px-2 py-1 text-[10px] bg-red-500/10 text-red-400 border border-red-500/30">
                      {sys}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recent Deployments */}
            {report.country_military_intel.recent_deployments && (
              <div>
                <div className="text-xs font-bold text-zinc-400 uppercase mb-2">Recent Deployments</div>
                <div className="flex flex-wrap gap-2">
                  {report.country_military_intel.recent_deployments.map((dep, idx) => (
                    <span key={idx} className="px-2 py-1 text-[10px] bg-orange-500/10 text-orange-400 border border-orange-500/30">
                      {dep}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Threat Assessment */}
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
              <div className="text-[10px] text-yellow-400 uppercase mb-1">Strategic Assessment</div>
              <div className="text-sm text-zinc-300">{report.country_military_intel.threat_assessment}</div>
            </div>
            
            {/* OSINT Sources */}
            <div className="text-[9px] text-zinc-500">
              Sources: {report.country_military_intel.osint_sources?.join(' • ')}
            </div>
          </div>
        </ReportSection>
      )}

      {/* Executive Summary (BLUF) */}
      <ExecutiveSummary 
        content={report.executive_summary}
        highlights={[
          `${report.detected_objects?.length || 0} Objects Identified`,
          `${report.threat_level || 'MODERATE'} Threat Level`,
          `${((report.overall_confidence_score || 0.7) * 100).toFixed(0)}% Confidence`
        ]}
      />

      {/* Location Context */}
      {report.location_context && (
        <LocationContextPanel 
          location={{ ...report.location, region: report.region }}
          context={report.location_context}
        />
      )}

      {/* Threat Assessment Panel */}
      <ThreatAssessmentPanel 
        assessment={report.threat_assessment}
        level={report.threat_level || 'MODERATE'}
        factors={report.activity_indicators?.slice(0, 5) || []}
      />

      {/* Detected Military Objects Section */}
      <ReportSection
        title="DETECTED MILITARY OBJECTS"
        icon={Crosshair}
        expanded={expandedSections.objects}
        onToggle={() => toggleSection('objects')}
        color="cyan"
        badge={`${report.detected_objects?.length || 0} IDENTIFIED`}
      >
        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
          {report.detected_objects?.map((obj, idx) => (
            <DetectedObjectCard 
              key={idx}
              object={obj}
              index={idx}
              expanded={selectedObject === idx}
              onToggle={(i) => setSelectedObject(selectedObject === i ? null : i)}
            />
          ))}
          {(!report.detected_objects || report.detected_objects.length === 0) && (
            <div className="text-center py-8 text-zinc-500">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No military objects detected in analysis area</p>
            </div>
          )}
        </div>
      </ReportSection>

      {/* Military Significance */}
      <ReportSection
        title="MILITARY SIGNIFICANCE"
        icon={Shield}
        expanded={expandedSections.military}
        onToggle={() => toggleSection('military')}
        color="orange"
      >
        <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
          {report.military_significance || 'Military significance assessment pending...'}
        </div>
      </ReportSection>

      {/* Infrastructure Analysis */}
      <ReportSection
        title="INFRASTRUCTURE ANALYSIS"
        icon={Layers}
        expanded={expandedSections.infrastructure}
        onToggle={() => toggleSection('infrastructure')}
        color="purple"
      >
        <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
          {report.infrastructure_analysis || 'Infrastructure analysis pending...'}
        </div>
      </ReportSection>

      {/* Activity Indicators */}
      {report.activity_indicators && report.activity_indicators.length > 0 && (
        <ReportSection
          title="ACTIVITY INDICATORS"
          icon={Activity}
          expanded={expandedSections.activity}
          onToggle={() => toggleSection('activity')}
          color="yellow"
          isList={true}
          content={report.activity_indicators}
        />
      )}

      {/* Source Reliability Matrix */}
      <SourceReliabilityMatrix sources={sourceReliability} />

      {/* Recommendations */}
      <RecommendationsPanel 
        recommendations={recommendations}
        priority={report.threat_level === 'CRITICAL' ? 'IMMEDIATE' : report.threat_level === 'HIGH' ? 'HIGH' : 'STANDARD'}
      />

      {/* Analyst Notes */}
      {report.analyst_notes && (
        <ReportSection
          title="ANALYST NOTES"
          icon={FileText}
          expanded={expandedSections.notes}
          onToggle={() => toggleSection('notes')}
          color="blue"
        >
          <div className="text-sm text-zinc-400 italic leading-relaxed whitespace-pre-wrap">
            {report.analyst_notes}
          </div>
        </ReportSection>
      )}

      {/* Export Actions */}
      <ExportActionsBar 
        onExportPPTX={onExport}
        exporting={exporting}
      />

      {/* Close Button */}
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="px-6 py-3 bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all rounded"
        >
          <span className="font-bold text-sm">CLOSE REPORT</span>
        </button>
      </div>
    </IntelligenceReport>
  );
}
// Change Detection Report Display Component
function ChangeReportDisplay({ report, images, expandedSections, toggleSection, onExport, onClose }) {
  return (
    <div className="space-y-4">
      {/* Classification Banner */}
      <div className={`p-3 text-center font-bold ${
        report.classification === 'SECRET' ? 'bg-red-500/20 text-red-400 border border-red-500/50'
        : report.classification === 'CONFIDENTIAL' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
        : 'bg-purple-500/10 text-purple-400 border border-purple-500/50'
      }`}>
        CHANGE DETECTION - {report.classification}
      </div>

      {/* Comparison Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-black/30 border border-white/10">
          <div className="text-[10px] text-zinc-500 uppercase mb-1">Before Image</div>
          <div className="text-sm font-bold">{images[0]?.satellite}</div>
          <div className="text-xs text-zinc-400">{new Date(images[0]?.acquisition_date).toLocaleDateString()}</div>
        </div>
        <div className="p-3 bg-black/30 border border-white/10">
          <div className="text-[10px] text-zinc-500 uppercase mb-1">After Image</div>
          <div className="text-sm font-bold">{images[1]?.satellite}</div>
          <div className="text-xs text-zinc-400">{new Date(images[1]?.acquisition_date).toLocaleDateString()}</div>
        </div>
      </div>

      {/* Executive Summary */}
      <ReportSection 
        title="EXECUTIVE SUMMARY" 
        content={report.executive_summary}
        expanded={expandedSections.executive}
        onToggle={() => toggleSection('executive')}
        color="purple-400"
      />

      {/* Detected Changes */}
      <div className="p-4 bg-black/30 border border-white/10">
        <button 
          onClick={() => toggleSection('changes')}
          className="w-full flex items-center justify-between mb-2"
        >
          <span className="text-sm font-bold text-purple-400">
            DETECTED CHANGES ({report.detected_changes?.length || 0})
          </span>
          {expandedSections.changes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.changes && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {report.detected_changes?.map((change, idx) => (
              <div key={idx} className="p-2 bg-white/5 border border-white/10 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold">{change.type}</span>
                  <span className={`px-2 py-0.5 text-[10px] ${
                    change.significance === 'HIGH' ? 'bg-red-500/10 text-red-400'
                    : change.significance === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400'
                    : 'bg-[#00FF9D]/10 text-[#00FF9D]'
                  }`}>
                    {change.significance}
                  </span>
                </div>
                <p className="text-zinc-400">{change.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Threat Assessment */}
      <ReportSection 
        title="THREAT IMPLICATION" 
        content={report.threat_implication}
        expanded={expandedSections.threat}
        onToggle={() => toggleSection('threat')}
        color="orange-400"
      />

      {/* Recommendations */}
      <div className="p-4 bg-black/30 border border-white/10">
        <button 
          onClick={() => toggleSection('recommendations')}
          className="w-full flex items-center justify-between mb-2"
        >
          <span className="text-sm font-bold text-[#00FF9D]">RECOMMENDATIONS</span>
          {expandedSections.recommendations ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.recommendations && (
          <ul className="space-y-2">
            {report.recommendations?.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                <Check className="w-4 h-4 text-[#00FF9D] mt-0.5 flex-shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onExport}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/10 text-purple-400 border border-purple-500/50 hover:bg-purple-500/20 transition-all"
        >
          <Download className="w-4 h-4" />
          <span className="font-bold text-sm">EXPORT PPTX</span>
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all"
        >
          <span className="font-bold text-sm">CLOSE</span>
        </button>
      </div>
    </div>
  );
}

